import {BaseModuleState} from "../../../infrastructure/models/base-module-state";
import {BaseMobxState} from "../../../infrastructure/mobx/base-mobx-state";
import {AppStore} from "../../../core/app/app-store";
import {LoggerUtils} from "../../../infrastructure/utils/logger/logger-utils";
import {DatetimeUtils} from "../../../infrastructure/utils/datetime-utils";
import {DeviceViewModel} from "../../../core/models/device.view-model";
import {TermsUtils} from "../../../infrastructure/utils/terms-utils";
import {MatchWidgetConfig} from "../match-widget.config";
import {NavigationManager} from "../../../core/managers/navigation-manager";
import {computed} from "mobx";
import {GAME_STATUSES, SPORT_TYPES} from "../../../core/app/consts";
import {ROUTE_NAMES} from "../../../core/consts";
import {MATCH_EVENTS} from "../modules/match-events-module/match-events-widget.consts";
import {FullGameDomainModel} from "../../../domain/full-game/models/full-game-domain-model";
import {CompetitionDomainModel} from "../../../domain/common/competition/models/competition-domain-model";
import {GameCenterLinkModel} from "../../../containers/game-center-container/models/game-center.link-model";
import {GameCenterHashParamsModel} from "../../../containers/game-center-container/models/game-center.hash-params-model";
import {ENTITY_DESCRIPTION} from "../../../modules/entity-description-module/entity-description.module.consts";
import {MATCH_WIDGET} from "../match-widget.consts";
import {LandingPageViewModel} from "../../../core/models/landing-page.view-model";
import {makeObservableClass} from "../../../infrastructure/decorators/observable";

@makeObservableClass
export class MatchWidgetState extends BaseModuleState {
  config: MatchWidgetConfig;

  static injectables = [...BaseMobxState.injectables, NavigationManager, AppStore, LoggerUtils, DatetimeUtils, DeviceViewModel, TermsUtils, MatchWidgetConfig];

  @computed get game(): FullGameDomainModel {
    return this.dataSource?.game;
  }

  @computed get gameId(): Number {
    return this.game?.id;
  }

  get statusGroup() {
    return this.game?.statusGroup;
  }

  @computed get homeCompetitorId(): Number {
    return this.game?.homeCompetitor.id;
  }

  @computed get awayCompetitorId(): Number {
    return this.game?.awayCompetitor.id;
  }

  @computed get competition(): CompetitionDomainModel {
    return this.game?.competition;
  }

  @computed get isTabsVisible(): Boolean {
    return this.game?.hasLineups || this.game?.hasMissingPlayers || this.game?.hasStats || this.game?.hasBets || this.game?.hasPlayerBets || this.game.hasPreviousMeetings || this.game.hasRecentMatches;
  }

  @computed get isScoreboard(): Boolean {
    return !this.config.sportTypesWithoutScoreboard.includes(this.game?.sport.id);
  }

  @computed get stageNum(): Number {
    return this.game?.stageNum;
  }

  @computed get seasonNum(): Number {
    return this.game?.seasonNum
  }

  @computed get shouldRenderTeaserOdds(): Boolean {
    const shouldRenderTeaserOdds = this.config.sportTypesToRenderTeaserOdds.includes(this.game?.sport.id) && this.showOdds;

    return shouldRenderTeaserOdds;
  }

  @computed get shouldRenderNextMatch(): Boolean {
    const shouldRenderNextMatch: Boolean = this.config.sportTypesToRenderTeaserOdds.includes(this.game?.sport.id);

    return shouldRenderNextMatch;
  }

  @computed get shouldRenderLineups(): Boolean {
    const notValidSportTypes = [SPORT_TYPES.baseball];

    const shouldRenderLineups: Boolean = !notValidSportTypes.includes(this.game?.sport.id) && ((!this.shouldRenderTopPerformers && !this.shouldRenderKeyPlayers) || this.game?.isScheduled);

    return shouldRenderLineups;
  }

  @computed get hasTopPerformers(): Boolean {
    const hasTopPerformers: Boolean = this.game?.topPerformers?.categories?.length > 0

    return hasTopPerformers;
  }

  @computed get shouldRenderTopPerformers(): Boolean {
    const shouldRenderTeaserOdds = this.hasTopPerformers && this.game?.sport.id === SPORT_TYPES.hockey && this.config.sportTypesToRenderTopPerformers.includes(this.game?.sport.id);

    return shouldRenderTeaserOdds;
  }

  @computed get shouldRenderLiveStreaming(): Boolean {
    const shouldRenderLiveStreaming: Boolean = this.game?.liveStreaming?.length > 0;

    return shouldRenderLiveStreaming
  }

  @computed get shouldRenderLastMatches(): Boolean {
    const shouldRenderLastMatches: Boolean = this.game?.atsRecords;

    return shouldRenderLastMatches
  }

  @computed get shouldRenderH2h(): Boolean {
    const shouldRenderH2h: Boolean = this.game?.previousMeetings?.length > 0 || this.game?.homeCompetitor?.recentMatches?.length > 0 || this.game?.awayCompetitor?.recentMatches?.length > 0;

    return shouldRenderH2h;
  }

  @computed get shouldRenderCurrentOddsModule(): Boolean {
    const shouldRenderCurrentOddsModule: Boolean = this.showOdds && !this.shouldRenderLiveOdds;

    return shouldRenderCurrentOddsModule
  }

  @computed get shouldRenderKeyPlayers(): Boolean {
    const notValidSportTypes = this.config.sportTypesToDisableKeyPlayers;

    if (notValidSportTypes.includes(this.game?.sport.id)) {
      return false;
    }

    const validSportTypesNotScheduled = [SPORT_TYPES.basketball, SPORT_TYPES.americanFootball];
    const isMatchScheduled: Boolean = this.game?.sport.id !== SPORT_TYPES.hockey;
    const shouldRenderKeyPlayers: Boolean = this.hasTopPerformers && ((!isMatchScheduled && validSportTypesNotScheduled.includes(this.game?.sport.id)) || isMatchScheduled);

    return shouldRenderKeyPlayers
  }

  @computed get shouldRenderMatchEvent(): Boolean {

    const shouldRenderMatchEvent: Boolean = !this.config.sportTypesDoNotRenderMatchEvents.includes(this.game?.sport.id);

    return shouldRenderMatchEvent
  }

  @computed get hasVideo(): Boolean {
    return this.dataSource.game?.hasVideo
  }

  @computed get video(): String {
    return this.dataSource.game?.video
  }

  @computed get isDirectLinkPage(): Boolean {
    const directRoutes: Array<String> = [ROUTE_NAMES.competition, ROUTE_NAMES.game];
    const isDirectLinkPage: Boolean = this.device.isSmallView || directRoutes.includes(this.currentLinkModel?.name);

    return isDirectLinkPage
  }

  @computed get showOdds(): Boolean {
    return this.appState.showOdds && this.appState.showSecondaryOdds;
  }

  @computed get gameStagesCount(): String {
    return this.game?.stages?.length - 1 || MATCH_EVENTS.PLACEHOLDERS.minimumStages
  }

  @computed get isGameRoute() {
    return this.currentLinkModel.name === ROUTE_NAMES.game
  }

  get gameCenterLinkModel(): GameCenterLinkModel {
    const hashParams: GameCenterHashParamsModel = new GameCenterHashParamsModel({id: this.game.id});

    return new GameCenterLinkModel(this.game.matchupId, this.game.sport.nameForURL, this.game.competition.nameForURL, this.game.nameForUrl, hashParams, this.game.isFollow);
  }

  @computed get gameEventsCount(): String {
    const eventsCount: Number = this.game?.events?.length || MATCH_EVENTS.PLACEHOLDERS.minimumRows;
    const topEventsCount: Number = this.game?.events?.filter(event => event.isMajor)?.length || MATCH_EVENTS.PLACEHOLDERS.minimumRows;

    return this.game?.isLive ? eventsCount : topEventsCount
  }

  @computed get shouldRenderCurrentPositions(): Boolean {
    const shouldRenderCurrentPositions = this.game.gameStageHasTable && this.appSettings.allowPremiumData;

    return shouldRenderCurrentPositions;
  }

  @computed get sportTypeId(): Number {
    return this.game?.sport.id;
  }

  @computed get shouldRenderGameShotsChart(): Boolean {
    const validSportTypesIds = [SPORT_TYPES.football, SPORT_TYPES.basketball];

    return !this.appState.settings.isWidget && validSportTypesIds.includes(this.sportTypeId);
  }

  @computed get shouldRenderActualPlayTime(): Boolean {
    return this.appSettings.allowPremiumData && this.actualPlayTime !== null;
  }

  @computed get shouldRenderTrendsWidget(): Boolean {
    return this.game.hasTrends;
  }

  @computed get shouldRenderOddsComparison(): Boolean {
    return this.showOdds && this.game.hasBets;
  }

  @computed get shouldRenderPropositionBets(): Boolean {
    return this.game.hasPlayerBets;
  }

  @computed get actualPlayTime(): Boolean {
    return this.game.actualPlayTime;
  }

  @computed get shouldRenderLiveOdds(): Boolean {
    return this.game.statusGroup === GAME_STATUSES.started && this.game.liveOdds;
  }

  @computed get shouldRenderMatchDescriptionModule(): Boolean {
    const type: Number = this.game.isScheduled ? ENTITY_DESCRIPTION.ENTITY_TYPE.preMatch : ENTITY_DESCRIPTION.ENTITY_TYPE.postMatch;
    const shouldRenderMatchDescriptionModule: Boolean = this.appState.entityDescriptionModel?.isValid(this.appState.context.sportType?.id, null, null, this.appState.language?.id, this.appState.locale?.userCountryId, type) && !this.game.isCancelledOrPostponed && !this.isWidget;

    return shouldRenderMatchDescriptionModule;
  }

  @computed get userCountryId() {
    return this.appState.locale?.userCountryId;
  }

  @computed get isWidget(): Boolean {
    return this.appState.settings.isWidget;
  }

  @computed get shouldRenderPreGameStats(): Boolean {
    return this.game?.sport.id === SPORT_TYPES.football && this.game.hasStats;
  }

  @computed get shouldRenderStats(): Boolean {
    return this.game.hasStats;
  }

  @computed get shouldRenderExternalAd(): Boolean {
    const shouldRenderExternalAd: Boolean = this.appState?.externalAdsModel?.shouldRender;

    return shouldRenderExternalAd
  }

  @computed get shouldRenderDBAIframe(): Boolean {
    const isUserCountryIdValid: Boolean = this.appState.locale.userCountryId === 21;
    const shouldRenderDBAIframe: Boolean = !this.appState.settings.isWidget && isUserCountryIdValid;

    return shouldRenderDBAIframe;
  }

  @computed get shouldRenderExternalAdOverDBA(): Boolean {
    const shouldRenderDBA = this.shouldRenderDBAIframe && !this.appState.externalAdsModel.remoteConfigModel.adsPrioritize;
    const shouldRenderExternalAd = this.shouldRenderExternalAd && !shouldRenderDBA;
    return shouldRenderExternalAd;
  }

  @computed get shouldRenderRelatedGames(): Boolean {
    const shouldRenderRelatedGames: Boolean = this?.game?.relatedGames?.length > 0

    return shouldRenderRelatedGames
  }

  @computed get topBookmaker(): Boolean {
    return this.appState.topBookmaker;
  }

  @computed get shouldRenderMatchFacts(): Boolean {
    return this.appSettings.allowPremiumData && this.game.matchFacts?.length > 0;
  }

  @computed get pageViewModel(): Boolean {
    return new LandingPageViewModel(MATCH_WIDGET.CONTAINER.ITEMS);
  }

  @computed get currentPointByPointGame() {
    return this.game.currentPointByPointGame
  }

  @computed get shouldRenderPointByPoint():Boolean {
    return this.game.hasPointByPoint && this.currentPointByPointGame
  }

  @computed get shouldRenderBetBoost():Boolean {
    return this.game.sport.id === SPORT_TYPES.football;
  }

  @computed get gridClassName(): Boolean {
    const gridType: Number = this.appState?.gridLayoutsModel?.gridLayoutToDisplay;

    return gridType > 0 ? `grid_${gridType}` : null;
  }

  @computed get shouldRenderBettingButton() {
    return this.appState.topBookmakerId && this.appState.shouldRenderBettingButton
  }

  @computed get shouldRenderOddsByPromotions() {
    return this.appState.shouldRenderOddsByPromotions;
  }
}
